import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Col, Form, FormGroup, Input, Label, Row, Spinner, Button } from 'reactstrap';
import Layout from '../components/Layout';
import api from '../services/api';

const getScoreTeamId = (entry) => {
  if (typeof entry?.league_team === 'number') return entry.league_team;
  if (typeof entry?.league_team?.id === 'number') return entry.league_team.id;
  if (typeof entry?.team === 'number') return entry.team;
  if (typeof entry?.team?.id === 'number') return entry.team.id;
  if (typeof entry?.team_id === 'number') return entry.team_id;
  return null;
};

const getScoreWeekId = (entry) => {
  if (typeof entry?.league_week === 'number') return entry.league_week;
  if (typeof entry?.league_week?.id === 'number') return entry.league_week.id;
  if (typeof entry?.week === 'number') return entry.week;
  if (typeof entry?.week_id === 'number') return entry.week_id;
  return null;
};

const getTeamDisplay = (leagueTeam) => {
  return leagueTeam?.team_display || leagueTeam?.team?.name || leagueTeam?.name || 'Unnamed Team';
};

const getLeagueTeamTeamId = (leagueTeam) => {
  if (typeof leagueTeam?.team === 'number') return leagueTeam.team;
  if (typeof leagueTeam?.team?.id === 'number') return leagueTeam.team.id;
  if (typeof leagueTeam?.team_id === 'number') return leagueTeam.team_id;
  return null;
};

const getCellKey = (leagueTeamId, weekId) => `${leagueTeamId}-${weekId}`;

const LeagueScoresAdmin = () => {
  const [leagues, setLeagues] = useState([]);
  const [selectedLeagueId, setSelectedLeagueId] = useState(null);
  const [weeks, setWeeks] = useState([]);
  const [leagueTeams, setLeagueTeams] = useState([]);
  const [weekScores, setWeekScores] = useState([]);
  const [draftsByCellKey, setDraftsByCellKey] = useState({});
  const [viewMode, setViewMode] = useState('team');
  const [showAllLeagues, setShowAllLeagues] = useState(false);
  const [loadingLeagues, setLoadingLeagues] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [loadingScores, setLoadingScores] = useState(false);
  const [error, setError] = useState('');
  const selectedLeague = leagues.find((league) => league.id === selectedLeagueId) || null;
  const selectedLeagueRouteId = selectedLeague?.guid || selectedLeagueId;

  useEffect(() => {
    const fetchLeagues = async () => {
      setLoadingLeagues(true);
      try {
        const response = await api.get('/leagues/');
        const allLeagues = Array.isArray(response.data) ? response.data : [];
        const leagueList = showAllLeagues
          ? allLeagues
          : allLeagues.filter((league) => league?.state === 'Active' || league?.state === 'Registration');
        setLeagues(leagueList);
        setSelectedLeagueId((current) => {
          if (leagueList.length === 0) {
            return null;
          }

          const currentExists = leagueList.some((league) => league.id === current);
          return currentExists ? current : leagueList[0].id;
        });
      } catch (err) {
        console.error('Error loading leagues:', err);
        setError('Failed to load leagues.');
      } finally {
        setLoadingLeagues(false);
      }
    };

    fetchLeagues();
  }, [showAllLeagues]);

  useEffect(() => {
    const fetchLeagueData = async () => {
      if (!selectedLeagueId) {
        setWeeks([]);
        setLeagueTeams([]);
        return;
      }

      setLoadingData(true);
      setError('');
      try {
        const [weeksResponse, teamsResponse] = await Promise.all([
          api.get(`/league-weeks/?league=${selectedLeagueId}`),
          api.get(`/league-teams/?league=${selectedLeagueId}`),
        ]);

        const loadedWeeks = Array.isArray(weeksResponse.data) ? weeksResponse.data : [];
        const loadedTeams = Array.isArray(teamsResponse.data) ? teamsResponse.data : [];

        loadedWeeks.sort((a, b) => (a?.week || 0) - (b?.week || 0));
        loadedTeams.sort((a, b) => getTeamDisplay(a).localeCompare(getTeamDisplay(b), undefined, { sensitivity: 'base' }));

        setWeeks(loadedWeeks);
        setLeagueTeams(loadedTeams);
      } catch (err) {
        console.error('Error loading league data:', err);
        setError('Failed to load league weeks or teams.');
      } finally {
        setLoadingData(false);
      }
    };

    fetchLeagueData();
  }, [selectedLeagueId]);

  const fetchWeekScores = useCallback(async () => {
    if (!selectedLeagueId) {
      setWeekScores([]);
      setDraftsByCellKey({});
      return;
    }

    setLoadingScores(true);
    setError('');
    try {
      const response = await api.get('/league-week-team-scores/', {
        params: {
          league: selectedLeagueId,
        },
      });

      const scores = Array.isArray(response.data) ? response.data : [];
      setWeekScores(scores);
    } catch (err) {
      console.error('Error loading team scores:', err);
      setError('Failed to load team scores.');
      setWeekScores([]);
    } finally {
      setLoadingScores(false);
    }
  }, [selectedLeagueId]);

  useEffect(() => {
    fetchWeekScores();
  }, [fetchWeekScores]);

  useEffect(() => {
    const nextDrafts = {};

    leagueTeams.forEach((team) => {
      const actualTeamId = getLeagueTeamTeamId(team);

      weeks.forEach((week) => {
        const existing = weekScores.find((entry) => {
          const entryTeamId = getScoreTeamId(entry);
          const entryWeekId = getScoreWeekId(entry);
          return entryTeamId === actualTeamId && entryWeekId === week.id;
        });

        nextDrafts[getCellKey(team.id, week.id)] = {
          id: existing?.id || null,
          score: existing?.score ?? '',
          saving: false,
        };
      });
    });

    setDraftsByCellKey(nextDrafts);
  }, [leagueTeams, weekScores, weeks]);

  const handleDraftChange = (teamId, weekId, value) => {
    const cellKey = getCellKey(teamId, weekId);
    setDraftsByCellKey((prev) => ({
      ...prev,
      [cellKey]: {
        ...(prev[cellKey] || {}),
        score: value,
      },
    }));
  };

  const setCellSaving = (teamId, weekId, saving) => {
    const cellKey = getCellKey(teamId, weekId);
    setDraftsByCellKey((prev) => ({
      ...prev,
      [cellKey]: {
        ...(prev[cellKey] || {}),
        saving,
      },
    }));
  };

  const updateCellDraft = (teamId, weekId, updates) => {
    const cellKey = getCellKey(teamId, weekId);
    setDraftsByCellKey((prev) => ({
      ...prev,
      [cellKey]: {
        ...(prev[cellKey] || {}),
        ...updates,
      },
    }));
  };

  const handlePersistCellScore = async (team, week) => {
    const cellKey = getCellKey(team.id, week.id);
    const draft = draftsByCellKey[cellKey];
    if (!selectedLeagueId || !draft || draft.saving) {
      return;
    }

    setCellSaving(team.id, week.id, true);

    try {
      const actualTeamId = getLeagueTeamTeamId(team);
      if (!actualTeamId) {
        alert(`Unable to resolve team id for ${getTeamDisplay(team)}.`);
        return;
      }

      const parsedScore = draft.score === '' ? null : Number(draft.score);

      if (draft.score !== '' && Number.isNaN(parsedScore)) {
        alert(`Score for ${getTeamDisplay(team)} must be a valid number.`);
        return;
      }

      if (parsedScore !== null && !Number.isInteger(parsedScore)) {
        alert(`Score for ${getTeamDisplay(team)} must be a whole number.`);
        return;
      }

      // Clearing an existing score deletes the record.
      if (parsedScore === null) {
        if (draft.id) {
          await api.delete(`/league-week-team-scores/${draft.id}/`);
          updateCellDraft(team.id, week.id, { id: null, score: '' });
        }
        return;
      }

      const payload = {
        league: selectedLeagueId,
        league_week: week.id,
        team: actualTeamId,
        score: parsedScore,
      };

      let savedResponse;
      if (draft.id) {
        savedResponse = await api.put(`/league-week-team-scores/${draft.id}/`, payload);
      } else {
        savedResponse = await api.post('/league-week-team-scores/', payload);
      }

      updateCellDraft(team.id, week.id, {
        id: savedResponse?.data?.id ?? draft.id,
        score: savedResponse?.data?.score ?? parsedScore,
      });
    } catch (err) {
      console.error('Error saving team score:', err);
      const detail = err.response?.data?.detail || err.message || 'Failed to save team score.';
      alert(`Error saving score for ${getTeamDisplay(team)}: ${detail}`);
    } finally {
      setCellSaving(team.id, week.id, false);
    }
  };

  return (
    <Layout>
      <div className="container my-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="mb-0">League Scores</h2>
          {selectedLeagueRouteId && (
            <Button
              tag="a"
              href={`/league/${selectedLeagueRouteId}/scores`}
              target="_blank"
              rel="noopener noreferrer"
              color="link"
              className="p-0"
            >
              Open Leaderboard
            </Button>
          )}
        </div>

        {error && <Alert color="danger">{error}</Alert>}

        {loadingLeagues ? (
          <p className="text-muted">Loading leagues...</p>
        ) : leagues.length === 0 ? (
          <p className="text-muted">No leagues found.</p>
        ) : (
          <>
            <Form className="mb-4">
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="league-score-league">League</Label>
                    <Input
                      id="league-score-league"
                      type="select"
                      value={selectedLeagueId || ''}
                      onChange={(e) => setSelectedLeagueId(Number(e.target.value))}
                    >
                      {leagues.map((league) => (
                        <option key={league.id} value={league.id}>
                          {league.title}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                  <FormGroup check className="mt-2">
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={showAllLeagues}
                        onChange={(e) => setShowAllLeagues(e.target.checked)}
                      />
                      {' '}Show all leagues
                    </Label>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="league-score-view">View</Label>
                    <Input
                      id="league-score-view"
                      type="select"
                      value={viewMode}
                      onChange={(e) => setViewMode(e.target.value)}
                    >
                      <option value="team">By Team</option>
                      <option value="week">By Week</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>
            </Form>

            {(loadingData || loadingScores) && (
              <div className="d-flex align-items-center gap-2 text-muted mb-3">
                <Spinner size="sm" />
                <span>Loading teams and scores...</span>
              </div>
            )}

            {!loadingData && weeks.length === 0 && (
              <p className="text-muted">This league has no weeks configured yet.</p>
            )}

            {!loadingData && weeks.length > 0 && leagueTeams.length === 0 && (
              <p className="text-muted">No teams found for this league.</p>
            )}

            {!loadingData && weeks.length > 0 && leagueTeams.length > 0 && viewMode === 'team' && (
              <div className="table-responsive">
                <table className="table table-bordered table-sm align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ minWidth: '220px' }}>Team</th>
                      {weeks.map((week) => (
                        <th key={week.id} style={{ minWidth: '150px' }}>
                          <div>Week {week.week}</div>
                          {week.notes && <div className="small text-muted fw-normal">{week.notes}</div>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {leagueTeams.map((team) => (
                      <tr key={team.id}>
                        <td className="fw-semibold">{getTeamDisplay(team)}</td>
                        {weeks.map((week) => {
                          const cellKey = getCellKey(team.id, week.id);
                          const draft = draftsByCellKey[cellKey] || { id: null, score: '', saving: false };

                          return (
                            <td key={`${team.id}-${week.id}`}>
                              <Input
                                type="number"
                                step="1"
                                bsSize="sm"
                                value={draft.score}
                                onChange={(e) => handleDraftChange(team.id, week.id, e.target.value)}
                                onBlur={() => handlePersistCellScore(team, week)}
                                placeholder="-"
                                disabled={draft.saving}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!loadingData && weeks.length > 0 && leagueTeams.length > 0 && viewMode === 'week' && (
              <div className="table-responsive">
                <table className="table table-bordered table-sm align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ minWidth: '150px' }}>Week</th>
                      {leagueTeams.map((team) => (
                        <th key={team.id} style={{ minWidth: '160px' }}>
                          {getTeamDisplay(team)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {weeks.map((week) => (
                      <tr key={week.id}>
                        <td className="fw-semibold">
                          <div>Week {week.week}</div>
                          {week.notes && <div className="small text-muted fw-normal">{week.notes}</div>}
                        </td>
                        {leagueTeams.map((team) => {
                          const cellKey = getCellKey(team.id, week.id);
                          const draft = draftsByCellKey[cellKey] || { id: null, score: '', saving: false };

                          return (
                            <td key={`${week.id}-${team.id}`}>
                              <Input
                                type="number"
                                step="1"
                                bsSize="sm"
                                value={draft.score}
                                onChange={(e) => handleDraftChange(team.id, week.id, e.target.value)}
                                onBlur={() => handlePersistCellScore(team, week)}
                                placeholder="-"
                                disabled={draft.saving}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default LeagueScoresAdmin;
